import { useState } from 'react';
import { ArrowLeft, Info, ChevronDown, ChevronUp, CheckCircle, Download, Share2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { toast } from 'sonner';
import { useRecordSpirometry } from '../lib/hooks/useTests';
import { useAuth } from '../lib/hooks/useAuth';

interface SpirometryTestProps {
  onBack?: () => void;
  patientInfo?: {
    name: string;
    age: number;
    gender: 'male' | 'female'
;
    height: number; // cm
    weight: number; // kg
  };
  selectedPatient?: { id: number; name: string }; // For physiotherapist/nurse roles
  userRole?: string; // To control UI based on role
}

export function SpirometryTest({ onBack, patientInfo, selectedPatient, userRole = 'patient' }: SpirometryTestProps) {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  // Use selectedPatient if provided (for therapist/nurse), otherwise use auth user
  const effectivePatientId = selectedPatient?.id || user?.patient_id;
  const patientName = selectedPatient?.name || user?.full_name || 'Patient';
  
  // ✅ DEBUG: Log auth state
  console.log('🔍 SpirometryTest Auth State:', {
    user,
    isAuthenticated,
    selectedPatient,
    effectivePatientId,
    authLoading
  });
  
  const recordSpirometryMutation = useRecordSpirometry();
  
  // Get patient info from props, selectedPatient (nurse mode), or auth user
  const patient = selectedPatient ? {
    name: selectedPatient.name || user?.name || 'Patient',
    age: selectedPatient.age || user?.age || 58,
    gender: (selectedPatient.gender || user?.gender || 'male') as 'male' | 'female',
    height: selectedPatient.height || user?.height || 170,
    weight: selectedPatient.weight || user?.weight || 72
  } : (patientInfo || {
    name: user?.name || 'Patient',
    age: user?.age || 58,
    gender: (user?.gender || 'male') as 'male' | 'female',
    height: user?.height || 170,
    weight: user?.weight || 72
  });
  
  const patientId = selectedPatient?.id || user?.patient_id || 0;
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [postBronchodilator, setPostBronchodilator] = useState(false);
  
  // Test preparation checklist
  const [checklist, setChecklist] = useState({
    noSmoking: false,
    noHeavyMeal: false,
    noVigorous: false,
    medicationTiming: false,
    restPeriod: false,
  });

  // Spirometry values
  const [fev1, setFev1] = useState('');
  const [fvc, setFvc] = useState('');
  const [pef, setPef] = useState('');
  const [fef2575, setFef2575] = useState('');
  const [fev3, setFev3] = useState('');

  // Calculate FEV1/FVC ratio
  const calculateRatio = () => {
    if (fev1 && fvc) {
      const ratio = (parseFloat(fev1) / parseFloat(fvc)) * 100;
      return ratio.toFixed(2);
    }
    return '';
  };

  // GLI-2022 Predicted values (simplified formula for demonstration)
  const getPredicted = (param: 'FEV1' | 'FVC' | 'PEF') => {
    const { age, height, gender } = patient;
    const heightM = height / 100;
    
    // Simplified GLI-2022 formulas (actual formulas are more complex)
    if (param === 'FEV1') {
      if (gender === 'male') {
        return (4.30 * heightM - 0.029 * age - 2.49).toFixed(2);
      } else {
        return (3.95 * heightM - 0.025 * age - 2.60).toFixed(2);
      }
    } else if (param === 'FVC') {
      if (gender === 'male') {
        return (5.76 * heightM - 0.026 * age - 4.34).toFixed(2);
      } else {
        return (4.43 * heightM - 0.026 * age - 2.89).toFixed(2);
      }
    } else if (param === 'PEF') {
      if (gender === 'male') {
        return ((6.14 * heightM - 0.043 * age + 0.15) * 60).toFixed(0);
      } else {
        return ((4.00 * heightM - 0.025 * age - 0.60) * 60).toFixed(0);
      }
    }
    return '0';
  };

  // Calculate z-score
  const calculateZScore = (actual: string, predicted: string) => {
    if (!actual || !predicted) return null;
    const a = parseFloat(actual);
    const p = parseFloat(predicted);
    // Simplified z-score calculation (actual formula uses RSD)
    const rsd = p * 0.15; // Approximate RSD as 15% of predicted
    const z = (a - p) / rsd;
    return z.toFixed(2);
  };

  // Calculate % predicted
  const calculatePercentPredicted = (actual: string, predicted: string) => {
    if (!actual || !predicted) return '';
    const percent = (parseFloat(actual) / parseFloat(predicted)) * 100;
    return percent.toFixed(1);
  };

  // Check if below LLN (z-score ≤ -1.645)
  const isBelowLLN = (zScore: string | null) => {
    if (!zScore) return false;
    return parseFloat(zScore) <= -1.645;
  };

  // Get values with calculations
  const fev1Predicted = getPredicted('FEV1');
  const fvcPredicted = getPredicted('FVC');
  const pefPredicted = getPredicted('PEF');
  
  const fev1ZScore = calculateZScore(fev1, fev1Predicted);
  const fvcZScore = calculateZScore(fvc, fvcPredicted);
  const ratio = calculateRatio();

  // Chart data for z-scores
  const chartData = [
    { 
      name: 'FEV1', 
      zScore: fev1ZScore ? parseFloat(fev1ZScore) : 0,
      fill: isBelowLLN(fev1ZScore) ? '#E84C3D' : '#14CAF4'
    },
    { 
      name: 'FVC', 
      zScore: fvcZScore ? parseFloat(fvcZScore) : 0,
      fill: isBelowLLN(fvcZScore) ? '#E84C3D' : '#14CAF4'
    },
    { 
      name: 'FEV1/FVC', 
      zScore: ratio ? ((parseFloat(ratio) - 75) / 10) : 0, // Simplified
      fill: '#2762AC'
    },
  ];

  // Clinical interpretation
  const getInterpretation = () => {
    if (!fev1 || !fvc) return 'Complete all required fields for interpretation';
    
    const fev1Low = isBelowLLN(fev1ZScore);
    const fvcLow = isBelowLLN(fvcZScore);
    const ratioValue = parseFloat(ratio);
    
    if (fev1Low && ratioValue < 70) {
      return 'FEV1 is below LLN and FEV1/FVC ratio is reduced; obstructive deficit probable (COPD, asthma).';
    } else if (fev1Low && fvcLow && ratioValue >= 70) {
      return 'Both FEV1 and FVC are below LLN with preserved ratio; restrictive pattern probable (pulmonary fibrosis, chest wall disorders).';
    } else if (fev1Low && !fvcLow) {
      return 'FEV1 is below LLN with normal FVC; mild obstructive pattern possible.';
    } else {
      return 'Spirometry values are within normal limits. No significant obstructive or restrictive deficit detected.';
    }
  };

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allChecklistComplete = Object.values(checklist).every(v => v);

  const handleSubmit = () => {
    if (!fev1 || !fvc) {
      toast.error('Required fields missing', {
        description: 'Please enter at least FEV1 and FVC values',
      });
      return;
    }

    if (!allChecklistComplete) {
      toast.error('Test preparation incomplete', {
        description: 'Please complete all checklist items before submitting',
      });
      return;
    }

    // ✅ CRITICAL: Check authentication FIRST before checking patient_id
    if (!isAuthenticated || (!selectedPatient && !user)) {
      console.error('❌ SpirometryTest - Not authenticated!', {
        isAuthenticated,
        user,
        selectedPatient,
        localStorage_user: localStorage.getItem('user'),
        localStorage_token: localStorage.getItem('access_token')
      });
      toast.error('Not Authenticated', {
        description: 'Your session has expired. Please log in again.',
      });
      return;
    }

    // ✅ Check if patient_id is valid (after auth check)
    if (!effectivePatientId || effectivePatientId === 0) {
      console.error('❌ SpirometryTest - No valid patient_id!', {
        effectivePatientId,
        selectedPatient,
        user,
        user_patient_id: user?.patient_id
      });
      toast.error('Unable to submit test', {
        description: 'Patient information not available. Please try logging out and logging in again.',
      });
      return;
    }

    // ✅ FIXED: Use correct field names that match backend API
    const spirometryData = {
      patient_id: effectivePatientId,
      fev1_liters: parseFloat(fev1),  // ✅ Correct field name
      fev1_percent_predicted: parseFloat(calculatePercentPredicted(fev1, fev1Predicted)),  // ✅ Correct field name
      fvc_liters: fvc ? parseFloat(fvc) : undefined,  // ✅ Correct field name
      fvc_percent_predicted: fvc ? parseFloat(calculatePercentPredicted(fvc, fvcPredicted)) : undefined,  // ✅ Correct field name
      fev1_fvc_ratio: ratio ? parseFloat(ratio) / 100 : undefined,  // ✅ Correct field name (as decimal)
      pef_liters_per_min: pef ? parseFloat(pef) : undefined,  // ✅ Correct field name
      quality_grade: allChecklistComplete ? 'A' : 'B', // ✅ FIXED: Backend expects quality_grade (A, B, C, D, F)
      test_location: selectedPatient ? 'home' : 'clinic',
      technician_notes: postBronchodilator ? 'Post-bronchodilator test' : 'Pre-bronchodilator test'  // ✅ FIXED: Backend expects technician_notes
    };

    console.log('✅ Submitting Spirometry data to backend:', spirometryData);

    // Use mutate from the mutation hook
    recordSpirometryMutation.mutate(spirometryData, {
      onSuccess: () => {
        toast.success('Spirometry test submitted successfully!', {
          description: 'Results have been saved and shared with your healthcare team',
        });
        // Reset form or navigate back
        if (onBack) onBack();
      },
      onError: (error: any) => {
        console.error('❌ Spirometry submission error:', error);
        toast.error('Failed to submit test results', {
          description: error.message || 'An error occurred while submitting the test results',
        });
      },
    });
  };

  const handleSaveDraft = () => {
    toast.info('Draft saved', {
      description: 'You can complete this test later',
    });
  };

  const handleDownload = () => {
    toast.success('Downloading report...', {
      description: 'PDF report will be downloaded shortly',
    });
  };

  const handleShare = () => {
    toast.success('Sharing with care team...', {
      description: 'Report sent to Dr. Meera Krishnan',
    });
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4 mb-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">Spirometry Test</h1>
            <p className="text-primary-foreground/80">
              {selectedPatient ? `Patient: ${patient.name}` : 'ATS/ERS 2019 Standardization'}
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                  <Info className="w-5 h-5 text-primary-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p>This test follows ATS/ERS 2019 technical standards for spirometry. GLI-2022 race-neutral reference equations are used for predicted values and z-scores.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Pre/Post Bronchodilator Toggle */}
        <div className="flex items-center gap-3 bg-primary-foreground/10 p-3 rounded-xl">
          <Label className="text-primary-foreground">Pre-Bronchodilator</Label>
          <Switch
            checked={postBronchodilator}
            onCheckedChange={setPostBronchodilator}
          />
          <Label className="text-primary-foreground">Post-Bronchodilator</Label>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Patient Details Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setDetailsExpanded(!detailsExpanded)}
            >
              <CardTitle className="text-primary">Patient Details</CardTitle>
              {detailsExpanded ? (
                <ChevronUp className="w-5 h-5 text-primary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-primary" />
              )}
            </div>
          </CardHeader>
          {detailsExpanded && (
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="text-foreground">{patient.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Age</p>
                  <p className="text-foreground">{patient.age} years</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Gender</p>
                  <p className="text-foreground capitalize">{patient.gender}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Height</p>
                  <p className="text-foreground">{patient.height} cm</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Weight</p>
                  <p className="text-foreground">{patient.weight} kg</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Test Preparation Checklist */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Test Preparation Checklist</CardTitle>
            <p className="text-muted-foreground">Ensure ATS/ERS 2019 quality prerequisites</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={checklist.noSmoking}
                onCheckedChange={() => handleChecklistChange('noSmoking')}
              />
              <div className="flex-1">
                <Label className="cursor-pointer">No smoking for at least 1 hour</Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Smoking can temporarily affect lung function measurements</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={checklist.noHeavyMeal}
                onCheckedChange={() => handleChecklistChange('noHeavyMeal')}
              />
              <div className="flex-1">
                <Label className="cursor-pointer">No heavy meal within 2 hours</Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Large meals can restrict diaphragm movement</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={checklist.noVigorous}
                onCheckedChange={() => handleChecklistChange('noVigorous')}
              />
              <div className="flex-1">
                <Label className="cursor-pointer">No vigorous exercise within 30 minutes</Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Rest period needed for baseline measurements</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={checklist.medicationTiming}
                onCheckedChange={() => handleChecklistChange('medicationTiming')}
              />
              <div className="flex-1">
                <Label className="cursor-pointer">Bronchodilator timing confirmed (if applicable)</Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Short-acting: withhold 4-6h, Long-acting: withhold 12-24h</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={checklist.restPeriod}
                onCheckedChange={() => handleChecklistChange('restPeriod')}
              />
              <div className="flex-1">
                <Label className="cursor-pointer">Patient rested for 5 minutes</Label>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Adequate rest period required before testing</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {allChecklistComplete && (
              <Alert className="bg-[#14CAF4]/10 border-[#14CAF4]">
                <CheckCircle className="h-4 w-4 text-[#14CAF4]" />
                <AlertTitle className="text-[#14CAF4]">Preparation Complete</AlertTitle>
                <AlertDescription>All prerequisites met. Ready to proceed with testing.</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Spirometry Data Entry */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Spirometry Data Entry</CardTitle>
            <p className="text-muted-foreground">
              {postBronchodilator ? 'Post-bronchodilator values' : 'Pre-bronchodilator values'}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* FEV1 */}
            <div className="space-y-3 p-4 bg-[#D4F0F8] rounded-xl">
              <div className="flex items-center justify-between">
                <Label className="text-primary">FEV1 (Liters) *</Label>
                {fev1 && (
                  <Badge 
                    variant="outline" 
                    className={isBelowLLN(fev1ZScore) ? 'bg-destructive/10 text-destructive border-destructive' : 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]'}
                  >
                    {isBelowLLN(fev1ZScore) ? 'Below LLN' : 'Normal'}
                  </Badge>
                )}
              </div>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 2.45"
                value={fev1}
                onChange={(e) => setFev1(e.target.value)}
                className="bg-white"
              />
              {fev1 && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">Predicted</p>
                    <p className="text-primary">{fev1Predicted} L</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">% Predicted</p>
                    <p className="text-primary">{calculatePercentPredicted(fev1, fev1Predicted)}%</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">z-score</p>
                    <p className={isBelowLLN(fev1ZScore) ? 'text-destructive' : 'text-primary'}>
                      {fev1ZScore}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* FVC */}
            <div className="space-y-3 p-4 bg-[#FEEBDA] rounded-xl">
              <div className="flex items-center justify-between">
                <Label className="text-primary">FVC (Liters) *</Label>
                {fvc && (
                  <Badge 
                    variant="outline" 
                    className={isBelowLLN(fvcZScore) ? 'bg-destructive/10 text-destructive border-destructive' : 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]'}
                  >
                    {isBelowLLN(fvcZScore) ? 'Below LLN' : 'Normal'}
                  </Badge>
                )}
              </div>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 3.20"
                value={fvc}
                onChange={(e) => setFvc(e.target.value)}
                className="bg-white"
              />
              {fvc && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">Predicted</p>
                    <p className="text-accent">{fvcPredicted} L</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">% Predicted</p>
                    <p className="text-accent">{calculatePercentPredicted(fvc, fvcPredicted)}%</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg">
                    <p className="text-muted-foreground">z-score</p>
                    <p className={isBelowLLN(fvcZScore) ? 'text-destructive' : 'text-accent'}>
                      {fvcZScore}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* FEV1/FVC Ratio */}
            {ratio && (
              <div className="space-y-3 p-4 bg-primary/10 rounded-xl">
                <Label className="text-primary">FEV1/FVC Ratio</Label>
                <div className="text-center p-4 bg-white rounded-lg">
                  <h2 className="text-primary">{ratio}%</h2>
                  <p className="text-muted-foreground">
                    {parseFloat(ratio) < 70 ? 'Obstructive pattern' : 'Normal ratio'}
                  </p>
                </div>
              </div>
            )}

            {/* PEF */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label>Peak Expiratory Flow (L/min)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Maximum flow rate during forced expiration</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="number"
                step="1"
                placeholder="e.g., 450"
                value={pef}
                onChange={(e) => setPef(e.target.value)}
              />
              {pef && (
                <p className="text-muted-foreground">
                  Predicted: {pefPredicted} L/min ({calculatePercentPredicted(pef, pefPredicted)}%)
                </p>
              )}
            </div>

            {/* FEF25-75 */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label>FEF 25-75% (L/s)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Forced mid-expiratory flow - sensitive for small airway disease</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 2.50"
                value={fef2575}
                onChange={(e) => setFef2575(e.target.value)}
              />
            </div>

            {/* FEV3 */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label>FEV3 (Liters)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Volume exhaled in first 3 seconds</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 3.00"
                value={fev3}
                onChange={(e) => setFev3(e.target.value)}
              />
            </div>

            <Alert className="bg-primary/5 border-primary/20">
              <Info className="h-4 w-4 text-primary" />
              <AlertTitle className="text-primary">GLI-2022 Reference Equations</AlertTitle>
              <AlertDescription>
                Predicted values use GLI-2022 race-neutral equations. LLN threshold: z-score ≤ -1.645 (5th percentile).
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Z-Score Chart */}
        {(fev1 || fvc) && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary">Z-Score Visualization</CardTitle>
              <p className="text-muted-foreground">Normal range: above -1.645</p>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                    <XAxis dataKey="name" stroke="#494949" />
                    <YAxis stroke="#494949" />
                    <RechartsTooltip />
                    <ReferenceLine y={-1.645} stroke="#E84C3D" strokeDasharray="3 3" label="LLN" />
                    <ReferenceLine y={0} stroke="#14CAF4" strokeDasharray="3 3" label="Mean" />
                    <Bar dataKey="zScore" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clinical Interpretation */}
        <Card className="shadow-lg bg-[#FEEBDA] border-accent">
          <CardHeader>
            <CardTitle className="text-primary">Clinical Interpretation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground">{getInterpretation()}</p>
            
            {(isBelowLLN(fev1ZScore) || isBelowLLN(fvcZScore)) && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Below Normal Range Detected</AlertTitle>
                <AlertDescription>
                  One or more parameters fall below the Lower Limit of Normal (LLN). 
                  This suggests potential lung function impairment. Your healthcare team will review these results.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share with Team
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="sticky bottom-4 bg-white p-4 rounded-xl shadow-lg border border-border space-y-3">
          <Button 
            className="w-full bg-accent hover:bg-accent/90 h-12"
            onClick={handleSubmit}
            disabled={!fev1 || !fvc || !allChecklistComplete}
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Submit Test Results
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleSaveDraft}
            >
              Save as Draft
            </Button>
            <Button 
              variant="ghost" 
              className="flex-1"
              onClick={onBack}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}